import { createApp } from 'vue';
import { getBugsnag } from './bugsnag';

// グローバルコンポーネント
import modal from './components/common/_modal';
import html5ValidationMessage from './components/common/_html5_validation_message';
import textareaPlaceholder from './components/common/placeholder/_textarea';
import textinputPlaceholder from './components/common/placeholder/_textinput';
import spectrumColorPicker from './components/common/_spectrum_color_picker';
import fontFamilySelecter from './components/common/_font_family_selecter';
import tooltip from './components/common/_tooltip';
import pageScroll from './components/common/_page_scroll';

/**
 * Vue3アプリケーションを作成するラッパー関数
 *
 * 処理内容
 * - Bugsnagの初期化と設定
 * - グローバルコンポーネントの登録
 * - マウント処理の実行（app.elが定義されている場合）
 *
 * @param {Object} app Vueアプリケーションの設定オブジェクト
 * @param {String} [app.el] マウント先のDOM要素セレクタ
 * @returns {App<Element>} 作成されたVueアプリケーションインスタンス
 */
export const createAppWrap = function (app) {
    if (!app || typeof app !== 'object') throw new Error('Invalid app configuration');

    const vueApp = createApp(app);

    // Bugsnagの初期化
    const bugsnag = getBugsnag();
    if (bugsnag) {
        vueApp.use(bugsnag);
    }

    // グローバルコンポーネントの登録
    // 登録: component('HogeFoo', hogeFoo)
    // 利用: <HogeFoo /> or <hoge-foo />
    vueApp
        .component('modal', modal)
        .component('html5-validation-message', html5ValidationMessage)
        .component('textarea-placeholder', textareaPlaceholder)
        .component('textinput-placeholder', textinputPlaceholder)
        .component('spectrum-color-picker', spectrumColorPicker)
        .component('font-family-selecter', fontFamilySelecter)
        .component('tooltip', tooltip)
        .component('page-scroll', pageScroll);

    // Vue3だと子孫コンポーネントをすべて事前に親コンポーネントへ登録する必要があるため共通処理で吸収
    const registered = Array.from(new Set([...Object.keys(vueApp._context.components), ...Object.keys(vueApp._component?.components || {})]));
    const registerComponents = (component) => {
        // コンポーネントが持つ子コンポーネントを取得
        const components = Object.assign(
            component?.components || {},
            Object.values(component?.mixins?.components || {}).reduce((acc, c) => { return { ...acc, c }; }, {})
        );
        if (Object.keys(components).length === 0) return;

        for (const [key, child] of Object.entries(components)) {
            // 重複していないコンポーネントを登録
            const name = (child.name || key).replace(/([A-Z])/g, '-$1').replace(/^-/, '').toLowerCase();
            if (!registered.includes(name)) {
                vueApp.component(name, child);

                // 登録済みコンポーネントを配列に追加
                registered.push(name);
            }

            // 再帰的に孫コンポーネントも登録
            registerComponents(child);
        }
    };
    registerComponents(app);

    // マウント先が指定されている場合はマウント
    if (typeof app.el !== 'undefined') {
        vueApp.mount(app.el);
    }

    return vueApp;
};
